import { memo } from 'react';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';

import { tiers } from '../../PricingPlans/plans';
import { questriOrange } from '../../../utils/questriButtonStyles';

const orderedTiers = [...tiers].reverse();

const TrialPlanSelector = ({
  value,
  onChange,
  disabled,
}) => {
  const billing = tiers.some((tier) => tier.selectPlan('annual') === value)
    ? 'annual'
    : 'monthly';
  const selectedTier = tiers.find((tier) => tier.selectPlan(billing) === value);

  return (
    <FormControl component="fieldset" fullWidth disabled={disabled}>
      <FormLabel
        component="legend"
        id="trial-plan-label"
        sx={{
          color: 'inherit',
          fontSize: 20,
          fontWeight: 600,
          mb: 1,
        }}
      >
        Choose your trial plan
      </FormLabel>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Explore Questri free for 14 days. Prices below apply after your trial, if you choose to subscribe.
      </Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={billing}
        disabled={disabled}
        aria-label="Billing period after the trial"
        onChange={(event, newBilling) => {
          if (newBilling && selectedTier) {
            onChange(selectedTier.selectPlan(newBilling));
          }
        }}
        sx={{
          alignSelf: 'flex-start',
          mb: 2,
          '& .MuiToggleButton-root.Mui-selected:not(.Mui-disabled)': {
            color: 'inherit',
            backgroundColor: alpha(questriOrange.main, 0.12),
            fontWeight: 600,
            '&:hover': { backgroundColor: alpha(questriOrange.main, 0.18) },
          },
        }}
      >
        <ToggleButton value="monthly">Monthly</ToggleButton>
        <ToggleButton value="annual">Annually · Save 10%</ToggleButton>
      </ToggleButtonGroup>
      <RadioGroup
        aria-labelledby="trial-plan-label"
        aria-describedby="trial-plan-helper"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'repeat(2, minmax(0, 1fr))',
          },
          gap: 2,
        }}
      >
        {orderedTiers.map((tier) => {
          const planValue = tier.selectPlan(billing);
          const selected = value === planValue;
          const price = billing === 'annual'
            ? tier.priceAnnual
            : tier.priceMonthly;
          const priceUnit = tier.name === 'Pay Per Horse'
            ? '/ horse / month'
            : '/ month';
          const backgroundColor = selected
            ? alpha(questriOrange.main, 0.08)
            : 'transparent';
          const borderColor = selected
            ? questriOrange.main
            : 'divider';
          let billingNote = 'Billed monthly after your trial';
          if (billing === 'annual') {
            billingNote = tier.name === 'Questri Pro'
              ? `${tier.priceAnnualTotal} billed annually after your trial`
              : `${tier.priceAnnualTotal} per horse billed annually after your trial`;
          }

          return (
            <FormControlLabel
              key={tier.name}
              value={planValue}
              disabled={disabled}
              control={(
                <Radio
                  inputProps={{ 'aria-label': tier.name }}
                  sx={{
                    color: questriOrange.main,
                    '&.Mui-checked': { color: questriOrange.main },
                  }}
                />
              )}
              sx={{
                m: 0,
                p: {
                  xs: 2,
                  sm: 2.5,
                },
                alignItems: 'flex-start',
                flexDirection: 'row-reverse',
                justifyContent: 'space-between',
                border: '1px solid',
                borderColor,
                borderRadius: 2,
                backgroundColor,
                '&:hover': { borderColor: questriOrange.main },
                '&:focus-within': {
                  outline: `2px solid ${questriOrange.main}`,
                  outlineOffset: 2,
                },
                '& .MuiFormControlLabel-label': {
                  flex: 1,
                  minWidth: 0,
                },
              }}
              label={(
                <Box component="span" sx={{ display: 'block' }}>
                  <Typography
                    component="span"
                    sx={{
                      display: 'block',
                      fontWeight: 600,
                      fontSize: 20,
                    }}
                  >
                    {tier.name}
                  </Typography>
                  {tier.name === 'Questri Pro' && (
                    <Chip
                      component="span"
                      label="Recommended"
                      size="small"
                      sx={{
                        my: 1,
                        maxWidth: '100%',
                      }}
                    />
                  )}
                  <Typography
                    component="span"
                    sx={{
                      display: 'block',
                      mt: 1,
                      mb: 0.5,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: 30,
                        fontWeight: 600,
                        letterSpacing: '-0.03em',
                      }}
                    >{price}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block',
                        ml: 0.5,
                        fontSize: 13,
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                      }}
                    >{priceUnit}
                    </Box>
                  </Typography>
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      mb: 2,
                    }}
                  >
                    {billingNote}
                  </Typography>
                  {tier.description.slice(0, 3).map((feature) => (
                    <Typography
                      component="span"
                      variant="body2"
                      key={feature}
                      sx={{
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      {feature}
                    </Typography>
                  ))}
                </Box>
              )}
            />
          );
        })}
      </RadioGroup>
      <FormHelperText
        id="trial-plan-helper"
        sx={{
          mx: 0,
          mt: 1.5,
        }}
      >
        No credit card required. Your plan determines which features you can use during the trial.
      </FormHelperText>
      <Accordion
        disableGutters
        elevation={0}
        sx={{
          mt: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px !important',
          backgroundColor: 'transparent',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreRounded />} id="trial-comparison-heading" aria-controls="trial-comparison-content">
          <Typography variant="body2" fontWeight={600}>Compare all plan features</Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              sm: 'repeat(2, minmax(0, 1fr))',
            },
            gap: 3,
          }}
        >
          {orderedTiers.map((tier) => (
            <Box key={tier.name}>
              <Typography component="h3" variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>{tier.name}</Typography>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  p: 0,
                  listStyle: 'none',
                }}
              >
                {tier.description.map((feature) => (
                  <Box
                    component="li"
                    key={feature}
                    sx={{
                      display: 'flex',
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <CheckRounded
                      fontSize="small"
                      color="success"
                      sx={{ flexShrink: 0 }}
                    />
                    <Typography variant="body2">{feature}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    </FormControl>
  );
};

export default memo(TrialPlanSelector);
